(() => {
  const colorImageNeedles = {
    Pink: 'cozy-heat-pink-charger-v1',
    White: 'cozy-heat-white-charger-v1',
  };

  const gallerySlides = (viewer) => {
    const list = viewer?.querySelector('ul[id^="Slider-Gallery-"]');
    return list ? [...list.querySelectorAll(':scope > li.product__media-item')] : [];
  };

  const getActiveSlide = (gallery, viewer, slides) => {
    const currentTarget = gallery
      ?.querySelector('.thumbnail[aria-current="true"]')
      ?.closest('[data-target]')
      ?.dataset?.target;
    return (
      slides.find((slide) => slide.classList.contains('is-active') || slide.getAttribute('aria-hidden') === 'false') ||
      slides.find((slide) => slide.dataset.mediaId === currentTarget) ||
      slides[0]
    );
  };

  const setGalleryButtons = (gallery, viewer, slides = gallerySlides(viewer)) => {
    const active = getActiveSlide(gallery, viewer, slides);
    const activeIndex = Math.max(0, slides.indexOf(active));
    const previous = viewer?.querySelector('button[name="previous"]');
    const next = viewer?.querySelector('button[name="next"]');

    if (previous) previous.toggleAttribute('disabled', activeIndex <= 0);
    if (next) next.toggleAttribute('disabled', activeIndex >= slides.length - 1);

    const current = viewer?.querySelector('.slider-counter--current');
    const total = viewer?.querySelector('.slider-counter--total');
    const currentText = String(activeIndex + 1);
    const totalText = String(slides.length);
    if (current && current.textContent !== currentText) current.textContent = currentText;
    if (total && total.textContent !== totalText) total.textContent = totalText;
  };

  const fallbackActivate = (gallery, viewer, slides, slide) => {
    slides.forEach((candidate) => {
      const active = candidate === slide;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-hidden', String(!active));
    });

    gallery.querySelectorAll('[data-target]').forEach((target) => {
      const thumbnail = target.querySelector('.thumbnail');
      if (!thumbnail) return;
      if (target.dataset.target === slide.dataset.mediaId) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    });

    viewer?.querySelector('ul[id^="Slider-Gallery-"]')?.scrollTo?.({
      left: slide.offsetLeft,
      behavior: 'smooth',
    });
  };

  const activateSlide = (gallery, viewer, slide) => {
    const slides = gallerySlides(viewer);
    if (!slide || !slides.includes(slide)) return;

    try {
      if (typeof gallery.setActiveMedia === 'function') {
        // Keep Dawn media handling available for video and modal synchronization.
        gallery.setActiveMedia(slide.dataset.mediaId, false);
      }
    } catch (_) {}

    // Always set the visible state ourselves, so either gallery arrow advances the requested slide.
    fallbackActivate(gallery, viewer, slides, slide);
    window.requestAnimationFrame(() => setGalleryButtons(gallery, viewer, gallerySlides(viewer)));
    window.setTimeout(() => {
      fallbackActivate(gallery, viewer, gallerySlides(viewer), slide);
      setGalleryButtons(gallery, viewer, gallerySlides(viewer));
    }, 80);
  };


  const bindGalleryArrowCapture = (gallery, viewer) => {
    if (gallery.dataset.yaznnerGalleryCaptureBound === 'true') return;
    gallery.dataset.yaznnerGalleryCaptureBound = 'true';

    if (window.yaznnerGalleryArrowCaptureReady === true) return;
    window.yaznnerGalleryArrowCaptureReady = true;

    window.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest?.('button[name="previous"], button[name="next"]');
        const activeGallery = button?.closest?.('media-gallery');
        if (!button || !activeGallery || activeGallery.dataset.yaznnerGalleryCaptureBound !== 'true' || button.disabled) return;

        const activeViewer = activeGallery.querySelector('slider-component[id^="GalleryViewer-"]');
        if (!activeViewer || !activeViewer.contains(button)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const slides = gallerySlides(activeViewer);
        if (slides.length < 2) return;

        const current = getActiveSlide(activeGallery, activeViewer, slides);
        const currentIndex = Math.max(0, slides.indexOf(current));
        const targetIndex =
          button.name === 'next'
            ? Math.min(slides.length - 1, currentIndex + 1)
            : Math.max(0, currentIndex - 1);

        activateSlide(activeGallery, activeViewer, slides[targetIndex]);
      },
      true
    );
  };

  const bindGalleryArrows = (gallery, viewer) => {
    viewer.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
      if (button.dataset.yaznnerGalleryArrowBound === 'true') return;
      button.dataset.yaznnerGalleryArrowBound = 'true';

      button.addEventListener(
        'click',
        (event) => {
          const slides = gallerySlides(viewer);
          if (slides.length < 2) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const current = getActiveSlide(gallery, viewer, slides);
          const currentIndex = Math.max(0, slides.indexOf(current));
          const targetIndex =
            button.name === 'next'
              ? Math.min(slides.length - 1, currentIndex + 1)
              : Math.max(0, currentIndex - 1);

          activateSlide(gallery, viewer, slides[targetIndex]);
        },
        true
      );
    });
  };

  const bindThumbnailSync = (gallery, viewer) => {
    gallery.querySelectorAll('.thumbnail').forEach((thumbnail) => {
      if (thumbnail.dataset.yaznnerGalleryThumbnailBound === 'true') return;
      thumbnail.dataset.yaznnerGalleryThumbnailBound = 'true';
      thumbnail.addEventListener('click', () => {
        window.setTimeout(() => setGalleryButtons(gallery, viewer, gallerySlides(viewer)), 80);
      });
    });
  };

  const setupGallery = (gallery) => {
    const viewer = gallery.querySelector('slider-component[id^="GalleryViewer-"]');
    if (!viewer) return;
    bindGalleryArrowCapture(gallery, viewer);
    bindGalleryArrows(gallery, viewer);
    bindThumbnailSync(gallery, viewer);
    setGalleryButtons(gallery, viewer);
  };

  const mediaIdForModalSlide = (gallery, modalSlide) => {
    const viewer = gallery?.querySelector('slider-component[id^="GalleryViewer-"]');
    return gallerySlides(viewer).find((slide) =>
      String(slide.dataset.mediaId || '').endsWith('-' + String(modalSlide.dataset.mediaId || ''))
    );
  };

  const setupProductModal = (modal) => {
    if (modal.dataset.yaznnerModalReady === 'true') return;
    modal.dataset.yaznnerModalReady = 'true';

    modal.querySelectorAll('[data-yn-modal-nav]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const slides = [...modal.querySelectorAll('.product-media-modal__content > [data-media-id]')];
        if (slides.length < 2) return;

        const current = slides.find((slide) => slide.classList.contains('active')) || slides[0];
        const currentIndex = Math.max(0, slides.indexOf(current));
        const targetIndex =
          button.dataset.ynModalNav === 'next'
            ? Math.min(slides.length - 1, currentIndex + 1)
            : Math.max(0, currentIndex - 1);
        const nextSlide = slides[targetIndex];

        slides.forEach((slide) => slide.classList.toggle('active', slide === nextSlide));
        nextSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        (nextSlide.matches('deferred-media') ? nextSlide : nextSlide.querySelector('deferred-media'))?.loadContent?.();

        const gallery = document
          .querySelector('modal-opener[data-modal="#' + modal.id + '"]')
          ?.closest('media-gallery');
        const viewer = gallery?.querySelector('slider-component[id^="GalleryViewer-"]');
        const mainSlide = gallery && mediaIdForModalSlide(gallery, nextSlide);
        if (gallery && viewer && mainSlide) activateSlide(gallery, viewer, mainSlide);
      });
    });
  };

  const showColorMedia = (color) => {
    const needle = colorImageNeedles[color];
    if (!needle) return;

    document.querySelectorAll('media-gallery').forEach((gallery) => {
      const viewer = gallery.querySelector('slider-component[id^="GalleryViewer-"]');
      const slide = gallerySlides(viewer).find((candidate) => candidate.querySelector('img[src*="' + needle + '"]'));
      if (viewer && slide) activateSlide(gallery, viewer, slide);
    });
  };

  const colorFromKachingInteraction = (root, target) => {
    const directValue = typeof target?.value === 'string' ? target.value : '';
    if (colorImageNeedles[directValue]) return directValue;

    const optionValue = target?.closest?.('[data-value]')?.dataset?.value;
    if (colorImageNeedles[optionValue]) return optionValue;

    const colorValue = target?.closest?.('[data-name="Color"][data-value]')?.dataset?.value;
    if (colorImageNeedles[colorValue]) return colorValue;

    const selected = [...root.querySelectorAll('[data-name="Color"][data-value]')].find(
      (element) =>
        element.getAttribute('aria-checked') === 'true' ||
        element.getAttribute('aria-selected') === 'true' ||
        element.classList.contains('selected') ||
        element.classList.contains('is-selected') ||
        element.classList.contains('active')
    );
    return selected?.dataset?.value;
  };

  const bundleBars = (root) => [...root.querySelectorAll('[data-deal-bar-id]')];

  const removeFourPack = (root) => {
    bundleBars(root).forEach((bar) => {
      const label = (bar.innerText || bar.textContent || '').trim();
      if (/\b(?:four|4)[\s-]*(?:pack|items?|x)\b/i.test(label)) bar.remove();
    });
  };

  const selectTrioByDefault = (root) => {
    if (root.dataset.yaznnerTrioDefaulted === 'true') return;
    const trioBar = bundleBars(root).find((bar) => {
      const label = (bar.innerText || bar.textContent || '').trim();
      return /\btrio\b|\b3\s*(pack|items?)\b/i.test(label);
    });
    const radio = trioBar?.querySelector('input[type="radio"]');
    if (!radio) return;
    if (!radio.checked) {
      const label = radio.labels?.[0] || root.querySelector('label[for="' + radio.id + '"]');
      if (label && typeof label.click === 'function') label.click();
      else if (typeof radio.click === 'function') radio.click();
    }
    root.dataset.yaznnerTrioDefaulted = 'true';
  };

  const setupKaching = (root) => {
    if (root.dataset.yaznnerColorSyncReady !== 'true') {
      root.dataset.yaznnerColorSyncReady = 'true';

      root.addEventListener('change', (event) => {
        window.setTimeout(() => {
          const color = colorFromKachingInteraction(root, event.target);
          if (color) showColorMedia(color);
        }, 80);
      });
      root.addEventListener('click', (event) => {
        window.setTimeout(() => {
          const color = colorFromKachingInteraction(root, event.target);
          if (color) showColorMedia(color);
        }, 120);
      });
    }

    removeFourPack(root);
    window.setTimeout(() => { removeFourPack(root); selectTrioByDefault(root); }, 180);
    window.setTimeout(() => { removeFourPack(root); selectTrioByDefault(root); }, 850);
  };


  const cleanWidgetCopy = () => {
    const productRoot = document.querySelector('main[data-template="product"]');
    if (!productRoot) return;

    document.querySelectorAll('.kaching-bundles').forEach((root) => removeFourPack(root));

    const vitalsTabs = document.querySelector('#vitals-tabs-placeholder');
    if (!vitalsTabs) return;

    const accordions = [...vitalsTabs.querySelectorAll('.vt-accordion')];
    const guarantee = accordions.find((element) => element.textContent.trim() === 'Our Gaurantee');
    if (guarantee) {
      [...guarantee.childNodes]
        .find((node) => node.nodeType === 3)
        ?.replaceWith('90 day money back guarantee ');
      const panel = guarantee.nextElementSibling;
      if (panel?.classList.contains('vt-panel') && !panel.textContent.includes('90 day money back guarantee')) {
        panel.innerHTML = '<p>Every Cozy Heat order includes a 90 day money back guarantee. Review our refund policy for the complete terms.</p>';
      }
    }

    const shipping = accordions.find((element) => element.textContent.trim() === 'SHIPPING');
    if (shipping) {
      const panel = shipping.nextElementSibling;
      if (panel?.classList.contains('vt-panel') && !panel.textContent.includes('Orders of $89+ qualify for free shipping')) {
        panel.innerHTML = '<p>Orders over $135.00 qualify for free standard shipping. Shipping is calculated at checkout for orders below that amount.</p>';
      }
    }
  };


  const clarifyKachingColors = (root) => {
    const blockTitle = root.querySelector('.kaching-bundles__block-title');
    if (blockTitle && !root.querySelector('.yn-kaching-color-helper')) {
      const helper = document.createElement('p');
      helper.className = 'yn-kaching-color-helper';
      helper.textContent = window.location.pathname.includes('/products/menstrual-heat-pad')
        ? 'Choose your Cozy Heat quantity here. A separate custom set is below.'
        : 'Choose your quantity here. A separate custom set is below.';
      blockTitle.insertAdjacentElement('afterend', helper);
    }

    root.querySelectorAll('.kaching-bundles__bar').forEach((bar) => {
      const units = [...bar.querySelectorAll('.kaching-bundles__bar-variant-selector-wrapper')];
      const variantName = bar.querySelector('.kaching-bundles__bar-variant-names span');

      const colorPrompt = 'Choose one matching color for your Cozy Heat set';
      if (units.length > 1 && variantName && variantName.textContent.trim() !== colorPrompt) {
        variantName.textContent = colorPrompt;
      }

      units.forEach((unit, index) => {
        const matchingSet = units.length > 1;
        const select = unit.querySelector('[role="combobox"]');
        if (select) {
          select.setAttribute('aria-label', matchingSet ? 'Matching Cozy Heat set color' : 'Cozy Heat color');
        }
        if (matchingSet && !unit.querySelector('.yn-kaching-unit-label')) {
          const label = document.createElement('span');
          label.className = 'yn-kaching-unit-label';
          label.textContent = 'Matching set color';
          unit.prepend(label);
        }
        if (matchingSet && index > 0) {
          unit.classList.add('yn-kaching-extra-color');
          unit.setAttribute('aria-hidden', 'true');
        }
      });
    });
  };

  const cleanLooxSetupState = () => {
    const setupMessages = ['Complete setup to activate widget', 'Enable the Loox Core Script'];

    document
      .querySelectorAll('loox-card-carousel, [data-loox-card-carousel], .loox-v2-carousel-container')
      .forEach((carousel) => {
        const copy = carousel.textContent || '';
        if (!setupMessages.some((message) => copy.includes(message))) return;

        const section = carousel.closest('.shopify-section');
        if (section) section.hidden = true;
        else carousel.hidden = true;
      });

    document.querySelectorAll('a[href*="loox.io"], a[href*="loox.com"]').forEach((link) => {
      if (/powered by/i.test((link.textContent || '').trim())) link.hidden = true;
    });
  };


  const showStudioGalleryOpening = () => {
    if (!document.querySelector('main[data-template="product"]')) return;
    if (window.location.pathname.includes('/products/menstrual-heat-pad')) return;

    document.querySelectorAll('media-gallery').forEach((gallery) => {
      if (gallery.dataset.yaznnerStudioOpeningReady === 'true') return;
      const viewer = gallery.querySelector('slider-component[id^="GalleryViewer-"]');
      const studioSlide = gallerySlides(viewer).find((slide) =>
        /blush pink studio background/i.test(slide.querySelector('img')?.alt || '')
      );
      if (!viewer || !studioSlide) return;

      gallery.dataset.yaznnerStudioOpeningReady = 'true';
      window.setTimeout(() => activateSlide(gallery, viewer, studioSlide), 420);
    });
  };

  const setupMobilePurchaseBar = () => {
    if (!document.querySelector('main[data-template="product"]')) return;

    const existingBar = document.querySelector('[data-yn-mobile-purchase-bar]');
    if (document.querySelector('.kaching-bundles')) {
      if (existingBar) existingBar.hidden = true;
      return;
    }

    const sourceButton = document.querySelector(
      'main[data-template="product"] form[action*="/cart/add"] .product-form__submit'
    );
    if (!sourceButton) return;

    let bar = existingBar;
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'yn-mobile-purchase-bar';
      bar.hidden = true;
      bar.setAttribute('data-yn-mobile-purchase-bar', '');
      bar.innerHTML = '<button type="button" class="button">Add selected set to cart</button>';
      document.body.append(bar);
    }

    const button = bar.querySelector('button');
    const sync = () => {
      button.disabled = sourceButton.disabled;
      button.setAttribute('aria-disabled', String(sourceButton.disabled));
    };
    sync();

    if (bar.dataset.ynMobilePurchaseBound === 'true') return;
    bar.dataset.ynMobilePurchaseBound = 'true';

    button.addEventListener('click', () => {
      sync();
      if (!sourceButton.disabled) sourceButton.click();
    });

    const updateVisibility = (isSourceVisible) => {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      bar.hidden = !mobile || isSourceVisible;
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => updateVisibility(entries[0]?.isIntersecting !== false),
        { threshold: 0.12 }
      );
      observer.observe(sourceButton);
    } else {
      updateVisibility(false);
    }

    window.addEventListener('resize', () => {
      if (!window.matchMedia('(max-width: 749px)').matches) bar.hidden = true;
    });
  };


  const formatComfortSetMoney = (cents) => {
    const numericCents = Number(cents) || 0;
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(numericCents / 100);
    } catch (_) {
      return '$' + (numericCents / 100).toFixed(2);
    }
  };

  const bundleProductVariants = (root) => {
    const source = root.querySelector('[data-yaznner-product-variants]')?.textContent?.trim();
    if (!source) return [];

    try {
      const variants = JSON.parse(source);
      return Array.isArray(variants) ? variants : [];
    } catch (_) {
      return [];
    }
  };

  const selectedVariantFromPicker = () => {
    const source = document.querySelector(
      'variant-radios script[data-selected-variant], variant-selects script[data-selected-variant]'
    )?.textContent?.trim();

    if (!source) return null;

    try {
      const variant = JSON.parse(source);
      return variant?.id ? variant : null;
    } catch (_) {
      return null;
    }
  };

  const selectedVariantFromKaching = (variants) => {
    const activeBar = document.querySelector('.kaching-bundles__bar--selected');
    if (!activeBar || !variants.length) return null;

    const chosenValue = [...activeBar.querySelectorAll('.kaching-bundles-option[data-value]')]
      .map((option) => option.dataset.value)
      .find(Boolean);
    if (!chosenValue) return null;

    return (
      variants.find((variant) => {
        const optionValues = Array.isArray(variant.options)
          ? variant.options.filter(Boolean)
          : [variant.option1, variant.option2, variant.option3].filter(Boolean);
        return optionValues.includes(chosenValue);
      }) || null
    );
  };

  const selectedMainVariantForSet = (root) => {
    const variants = bundleProductVariants(root);
    const pickerVariant = selectedVariantFromPicker();
    if (pickerVariant) return pickerVariant;

    const kachingVariant = selectedVariantFromKaching(variants);
    if (kachingVariant) return kachingVariant;

    const mainVariantInput = document.querySelector(
      'main[data-template="product"] product-form form[action*="/cart/add"] [name="id"], main[data-template="product"] .product-form form[action*="/cart/add"] [name="id"]'
    );
    const formVariantId = String(mainVariantInput?.value || '');
    if (formVariantId) {
      return variants.find((variant) => String(variant.id) === formVariantId) || { id: formVariantId };
    }

    const fallbackId = String(root.dataset.mainDefaultVariant || '');
    return variants.find((variant) => String(variant.id) === fallbackId) || { id: fallbackId };
  };

  const setupComfortSetBuilder = (root) => {
    if (root.dataset.yaznnerComfortSetReady === 'true') return;
    root.dataset.yaznnerComfortSetReady = 'true';

    const setScope = root.closest('.yn-product-upsells, .yn-comfort-pairings') || root;
    const options = [...setScope.querySelectorAll('[data-yaznner-set-option]')];
    const maximumAddOns = Number(root.dataset.maxAddons || 3);
    const minimumAddOns = 1;
    const count = root.querySelector('[data-yaznner-set-count]');
    const total = root.querySelector('[data-yaznner-set-total]');
    const status = root.querySelector('[data-yaznner-set-status]');
    const button = root.querySelector('[data-yaznner-add-set]');
    const setStatus = (message = '') => {
      if (status) status.textContent = message;
    };

    const updateOptionAvailability = (selected) => {
      const atLimit = selected.length >= maximumAddOns;
      options.forEach((option) => {
        const unavailable = atLimit && !option.checked;
        option.disabled = unavailable;
        option.setAttribute('aria-disabled', String(unavailable));
        option.closest('label')?.classList.toggle('is-unavailable', unavailable);
      });
    };

    const refresh = () => {
      const selected = options.filter((option) => option.checked);
      const selectedMainVariant = selectedMainVariantForSet(root);
      const initialPrice = Number(selectedMainVariant?.price ?? root.dataset.mainPriceCents) || 0;
      const price = selected.reduce((sum, option) => sum + (Number(option.dataset.priceCents) || 0), initialPrice);
      const ready = selected.length >= minimumAddOns;
      const totalItems = selected.length + 1;

      if (selectedMainVariant?.id) root.dataset.yaznnerSelectedVariant = String(selectedMainVariant.id);
      if (count) count.textContent = `${totalItems} item${totalItems === 1 ? '' : 's'}`;
      if (total) total.textContent = formatComfortSetMoney(price);
      updateOptionAvailability(selected);

      if (button && button.dataset.yaznnerSubmitting !== 'true') {
        button.disabled = !ready;
        button.setAttribute('aria-disabled', String(!ready));
        button.textContent = ready ? 'Add selected items to cart' : 'Choose a companion';
      }

      if (!ready) {
        setStatus('Choose at least one companion to make a custom set.');
      } else if (selected.length === maximumAddOns) {
        setStatus('All three companion pieces are included in your custom set.');
      } else {
        setStatus(`Add up to ${maximumAddOns - selected.length} more companion${maximumAddOns - selected.length === 1 ? '' : 's'}, or add your selected items to cart.`);
      }
    };

    options.forEach((option) => {
      option.addEventListener('change', (event) => {
        if (options.filter((choice) => choice.checked).length > maximumAddOns) {
          event.currentTarget.checked = false;
        }
        refresh();
      });
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches?.('main[data-template="product"] [name="id"]')) {
        window.setTimeout(refresh, 0);
      }
    });

    button?.addEventListener('click', async () => {
      const selected = options.filter((option) => option.checked);
      if (selected.length < minimumAddOns) {
        refresh();
        return;
      }

      const selectedMainVariant = selectedMainVariantForSet(root);
      const mainVariantId = String(selectedMainVariant?.id || '');
      if (!mainVariantId) {
        setStatus('Choose an available option before adding your set.');
        return;
      }

      const items = [
        { id: Number(mainVariantId), quantity: 1 },
        ...selected.map((option) => ({ id: Number(option.dataset.variantId), quantity: 1 })),
      ].filter((item) => Number.isFinite(item.id) && item.id > 0);

      button.dataset.yaznnerSubmitting = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      setStatus('Adding your selected items to cart.');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error('Could not add the bundle');
        window.location.assign('/cart');
      } catch (_) {
        button.dataset.yaznnerSubmitting = 'false';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        refresh();
        setStatus('We could not add that set. Please try again.');
      }
    });

    refresh();
  };

  const initializeStatic = () => {
    document.querySelectorAll('media-gallery').forEach(setupGallery);
    document.querySelectorAll('product-modal').forEach(setupProductModal);
    document.querySelectorAll('[data-yaznner-comfort-set]').forEach(setupComfortSetBuilder);
    setupMobilePurchaseBar();
  };

  const initializeWidgets = () => {
    document.querySelectorAll('.kaching-bundles').forEach((root) => {
      setupKaching(root);
      clarifyKachingColors(root);
    });
    cleanWidgetCopy();
    cleanLooxSetupState();
    setupMobilePurchaseBar();
  };


  const observeKachingWidgets = () => {
    if (document.documentElement.dataset.yaznnerKachingObserver === 'true') return;
    document.documentElement.dataset.yaznnerKachingObserver = 'true';

    const clean = () => {
      document.querySelectorAll('.kaching-bundles').forEach((root) => removeFourPack(root));
    };

    const observer = new MutationObserver(clean);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    clean();
  };

  const boot = () => {
    observeKachingWidgets();
    initializeStatic();
    showStudioGalleryOpening();

    let widgetPasses = 0;
    const initializeLateWidgets = () => {
      widgetPasses += 1;
      initializeWidgets();
      if (widgetPasses < 12) {
        window.setTimeout(initializeLateWidgets, 800);
      }
    };

    initializeLateWidgets();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();

